import React, { useState, useCallback, useEffect, ChangeEvent } from 'react'
import { IoMdClose, IoMdCheckmark } from 'react-icons/io'
import { LuChevronsUpDown } from 'react-icons/lu'

import { Modal } from '@components/common/modal'
import { Input, Checkbox } from '@components/common/form'

import { AccountSearchParams } from './MarketplaceUsersPage'
import {
  IndexAccount,
  MarketplaceUser,
  CreateMarketplaceUserParams,
  RailsContact,
} from '@types'

interface CreateMarketplaceUserModalProps {
  currentHq: number
  hideModal: () => void
  loadAccounts: (params: AccountSearchParams) => Promise<IndexAccount[]>
  createMarketplaceUser: (
    params: CreateMarketplaceUserParams,
  ) => Promise<MarketplaceUser | null>
  getContact: (email: string) => Promise<RailsContact | null>
  refreshMarketplaceUsers: (page: number) => void
}

const CreateMarketplaceUserModal: React.FC<CreateMarketplaceUserModalProps> = ({
  currentHq,
  hideModal,
  loadAccounts,
  createMarketplaceUser,
  getContact,
  refreshMarketplaceUsers,
}) => {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')

  const [searchTerm, setSearchTerm] = useState('')
  const [sendWelcome, setSendWelcome] = useState(false)
  const [accounts, setAccounts] = useState<IndexAccount[]>([])
  const [selectedClient, setSelectedClient] = useState<IndexAccount | null>(
    null,
  )
  const [openCombobox, setOpenCombobox] = useState(false)
  const [linkAccounts, setLinkAccounts] = useState<
    { id: string; name: string }[]
  >([])

  const searchAccounts = useCallback(async () => {
    const accounts = await loadAccounts({
      search: searchTerm,
      searchAllHqs: true,
      page: 1,
      resultsPerPage: 10,
    })
    setAccounts(accounts)
  }, [loadAccounts, searchTerm])

  useEffect(() => {
    searchAccounts()
  }, [searchAccounts])

  const onSave = async () => {
    const accountIds = linkAccounts.map((account) => account.id)
    if (selectedClient) {
      accountIds.push(selectedClient.id)
    }

    const params: CreateMarketplaceUserParams = {
      firstName,
      lastName,
      email,
      headquarterId: currentHq,
      accountIds: [...new Set(accountIds)],
      sendWelcomeEmail: sendWelcome,
    }

    const newMarketplaceUser = await createMarketplaceUser(params)

    if (newMarketplaceUser) {
      refreshMarketplaceUsers(1)
      hideModal()
    }
  }

  const newUserHeader = () => {
    return (
      <div className="flex flex-col items-center w-full">
        <div className="flex flex-row justify-between items-center w-full">
          <p className="text-2xl font-bold">Create New Marketplace User</p>
          <button
            className="bg-transparent border-none cursor-pointer"
            onClick={hideModal}
          >
            <IoMdClose size={30} />
          </button>
        </div>
        <p className="text-sm text-gray-500 w-full text-left">
          Create a new admin for a client. You can choose to send a welcome
          email now or later.
        </p>
      </div>
    )
  }

  const renderLinkAccount = () => {
    return (
      <div className="flex flex-col w-full">
        <p className="text-lg font-bold">Select Account to Link User to</p>
        <div className="my-2">
          <div className="flex items-center border-2 rounded-md border-gray-400">
            <div className="px-3 py-2 border-r-2 border-gray-400">
              <IoMdCheckmark color="#cbd5e0" />
            </div>
            <input
              type="text"
              className="flex-1 px-3 py-2 outline-none"
              placeholder="Search for a client profile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={() => setOpenCombobox(true)}
            />
            <button
              className="px-3 bg-transparent border-none cursor-pointer"
              onClick={() => setOpenCombobox(!openCombobox)}
            >
              <LuChevronsUpDown color="#cbd5e0" />
            </button>
          </div>

          {openCombobox && (
            <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
              {accounts.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  No client profile found
                </div>
              ) : (
                accounts.map((account, idx) => (
                  <div
                    key={idx}
                    className={`px-3 py-2 cursor-pointer flex items-center ${
                      selectedClient && selectedClient.id === account.id
                        ? 'bg-gray-100'
                        : ''
                    } hover:bg-gray-100`}
                    onClick={() => {
                      setSelectedClient(account)
                      setSearchTerm(account.name)
                      setOpenCombobox(false)
                    }}
                  >
                    {selectedClient && selectedClient.id === account.id && (
                      <IoMdCheckmark color="#48bb78" />
                    )}
                    <span>{account.name}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        {selectedClient && (
          <p className="text-sm text-green-600 mt-1">
            <IoMdCheckmark className="inline-block h-4 w-4 mr-1" />
            Selected: {selectedClient.name}
          </p>
        )}
        {linkAccounts.length > 0 &&
          linkAccounts.map((account, idx) => (
            <p key={`account-${idx}`} className="text-sm text-green-600 mt-1">
              <IoMdCheckmark className="inline-block h-4 w-4 mr-1" />
              {account.name}{' '}
              <IoMdClose
                className="inline-block h-4 w-4 ml-1"
                color="red"
                onClick={() =>
                  setLinkAccounts(
                    linkAccounts.filter((a) => a.id !== account.id),
                  )
                }
              />
            </p>
          ))}
      </div>
    )
  }

  return (
    <Modal hideModal={hideModal} noHeader padding="10px">
      {newUserHeader()}
      <div className="mt-2 w-full p-3">
        <div className="flex flex-row gap-2 justify-between">
          <Input
            label="First Name"
            width="45%"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <Input
            label="Last Name"
            width="45%"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>
        <Input
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={async () => {
            const contact = await getContact(email)
            if (contact) {
              setFirstName(contact.firstName)
              setLastName(contact.lastName)
              setLinkAccounts(contact.accounts)
            } else {
              setLinkAccounts([])
            }
          }}
        />
      </div>
      <div className="mt-2 w-full p-3">{renderLinkAccount()}</div>
      <div className="mt-2 w-full p-3">
        <Checkbox
          label="Send welcome email with account creation"
          checked={sendWelcome}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setSendWelcome(e.target.checked)
          }
          checkedColor="black"
          labelStyle="font-semibold text-lg"
          description={
            !sendWelcome
              ? 'The account will be created without sending a welcome email. You can send the welcome email later from the user management page.'
              : undefined
          }
        />
      </div>
      <div className="flex flex-row gap-2 justify-end mt-3">
        <button
          className="bg-white text-black border border-black px-4 py-2 rounded-md"
          style={{ fontWeight: 600 }}
          onClick={() => hideModal()}
        >
          Cancel
        </button>
        <button
          className="bg-black text-white px-4 py-2 rounded-md"
          style={{ fontWeight: 600 }}
          onClick={onSave}
        >
          Create User
        </button>
      </div>
    </Modal>
  )
}

export default CreateMarketplaceUserModal
